import { useState, useEffect, useMemo } from 'react'
import { hospitalesAPI } from '../services/api'
import { useAuthStore, ROLES } from '../store'
import {
  Search, Building2, Activity, Users, MapPin,
  Download, X, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, Clock, Bed, Stethoscope, Phone, Mail
} from 'lucide-react'

// Datos demo
const DEMO_HOSPITALES = [
  { id: 1, nombre: 'HGZ No. 53 Los Reyes', clave: 'HGZ-53', tipo: 'HGZ', direccion: 'Av. de los Reyes 123, La Paz', telefono: '55-1234-5678', email: 'hgz53@imss.gob.mx', quirofanos_totales: 8, quirofanos_disponibles: 3, quirofanos_en_uso: 5, ocupacion: 78, camas: 245, camas_disponibles: 54, personal_medico: 180, cirugias_hoy: 12 },
  { id: 2, nombre: 'HGZ No. 57 La Quebrada', clave: 'HGZ-57', tipo: 'HGZ', direccion: 'Calle Quebrada 456, Neza', telefono: '55-2345-6789', email: 'hgz57@imss.gob.mx', quirofanos_totales: 6, quirofanos_disponibles: 1, quirofanos_en_uso: 5, ocupacion: 85, camas: 198, camas_disponibles: 30, personal_medico: 145, cirugias_hoy: 8 },
  { id: 3, nombre: 'HGZ No. 68 Tulpetlac', clave: 'HGZ-68', tipo: 'HGZ', direccion: 'Blvd. Tulpetlac 789, Ecatepec', telefono: '55-3456-7890', email: 'hgz68@imss.gob.mx', quirofanos_totales: 10, quirofanos_disponibles: 4, quirofanos_en_uso: 6, ocupacion: 62, camas: 312, camas_disponibles: 119, personal_medico: 220, cirugias_hoy: 15 },
  { id: 4, nombre: 'HGZ No. 76 Xalostoc', clave: 'HGZ-76', tipo: 'HGZ', direccion: 'Av. Xalostoc 321, Ecatepec', telefono: '55-4567-8901', email: 'hgz76@imss.gob.mx', quirofanos_totales: 5, quirofanos_disponibles: 0, quirofanos_en_uso: 5, ocupacion: 91, camas: 156, camas_disponibles: 14, personal_medico: 98, cirugias_hoy: 6 },
  { id: 5, nombre: 'HGR No. 196 Ecatepec', clave: 'HGR-196', tipo: 'HGR', direccion: 'Av. Central 654, Ecatepec', telefono: '55-5678-9012', email: 'hgr196@imss.gob.mx', quirofanos_totales: 12, quirofanos_disponibles: 3, quirofanos_en_uso: 9, ocupacion: 73, camas: 380, camas_disponibles: 103, personal_medico: 290, cirugias_hoy: 22 },
  { id: 6, nombre: 'HGZ No. 197 Texcoco', clave: 'HGZ-197', tipo: 'HGZ', direccion: 'Calle Texcoco 987, Texcoco', telefono: '55-6789-0123', email: 'hgz197@imss.gob.mx', quirofanos_totales: 4, quirofanos_disponibles: 2, quirofanos_en_uso: 2, ocupacion: 55, camas: 120, camas_disponibles: 54, personal_medico: 85, cirugias_hoy: 4 },
]

// Contador animado
const AnimatedCounter = ({ value, duration = 1000, showDashOnZero = false }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const end = parseInt(value) || 0
    if (end === 0) {
      setCount(0)
      return
    }

    const startTime = Date.now()
    const startValue = 0

    const animate = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / duration, 1)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      const current = Math.round(startValue + (end - startValue) * easeOut)

      setCount(current)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [value, duration])

  if (showDashOnZero && (!value || value === 0)) {
    return <span>--</span>
  }

  return <span>{count}</span>
}

// KPI Card mejorado
const KPICard = ({ title, value, subtitle, icon: IconComponent, trend, trendValue, color, onClick, isActive, showDashOnZero = false, suffix = '' }) => {
  const colorClasses = {
    emerald: 'from-emerald-500 to-emerald-600',
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600',
    teal: 'from-teal-500 to-teal-600',
    green: 'from-green-500 to-green-600'
  }

  const showDash = showDashOnZero && (!value || value === 0)

  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colorClasses[color]} p-5 text-white shadow-lg transition-all duration-300 ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-xl' : ''} ${isActive ? 'ring-4 ring-white/50 scale-[1.02]' : ''}`}
    >
      {isActive && (
        <div className="absolute top-2 right-2">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <div className="text-4xl font-bold mb-1 tracking-tight">
            {showDash ? '--' : <><AnimatedCounter value={value} />{suffix}</>}
          </div>
          <p className="text-white/70 text-xs">{subtitle}</p>
        </div>
        <div className="p-3 bg-white/20 rounded-xl">
          <IconComponent className="w-6 h-6" />
        </div>
      </div>
      {trend && !showDash && (
        <div className="mt-3 flex items-center gap-1 text-sm">
          {trend === 'up' ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          <span className="text-white/90">{trendValue} vs mes anterior</span>
        </div>
      )}
    </div>
  )
}

// Modal de detalle del hospital
const HospitalModal = ({ hospital, onClose }) => {
  if (!hospital) return null

  const getOcupacionColor = (ocupacion) => {
    if (ocupacion >= 85) return 'text-red-600'
    if (ocupacion >= 70) return 'text-amber-600'
    return 'text-green-600'
  }

  const getOcupacionBg = (ocupacion) => {
    if (ocupacion >= 85) return 'bg-red-500'
    if (ocupacion >= 70) return 'bg-amber-500'
    return 'bg-green-500'
  }

  const getStatusBadge = (ocupacion) => {
    if (ocupacion >= 85) return { text: 'Critico', bg: 'bg-red-100 text-red-700' }
    if (ocupacion >= 70) return { text: 'Alta demanda', bg: 'bg-amber-100 text-amber-700' }
    return { text: 'Normal', bg: 'bg-green-100 text-green-700' }
  }

  const status = getStatusBadge(hospital.ocupacion)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{hospital.nombre}</h2>
            <p className="text-sm text-gray-500">{hospital.clave}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status y tipo */}
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg}`}>
              {status.text}
            </span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
              {hospital.tipo || 'HGZ'}
            </span>
          </div>

          {/* Informacion de contacto */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-gray-800 mb-3">Informacion de contacto</h3>
            <div className="flex items-center gap-3 text-sm">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{hospital.direccion || 'Sin direccion registrada'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Phone className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{hospital.telefono || 'Sin telefono'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{hospital.email || 'Sin email'}</span>
            </div>
          </div>

          {/* Ocupacion general */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-800">Ocupacion general</h3>
              <span className={`text-2xl font-bold ${getOcupacionColor(hospital.ocupacion)}`}>
                {hospital.ocupacion || 0}%
              </span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getOcupacionBg(hospital.ocupacion)}`}
                style={{ width: `${hospital.ocupacion || 0}%` }}
              />
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <Activity className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">{hospital.quirofanos_totales || 0}</p>
              <p className="text-xs text-gray-500">Quirofanos</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{hospital.quirofanos_disponibles || 0}</p>
              <p className="text-xs text-gray-500">Disponibles</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-600">{hospital.quirofanos_en_uso || 0}</p>
              <p className="text-xs text-gray-500">En uso</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <Stethoscope className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">{hospital.cirugias_hoy || 0}</p>
              <p className="text-xs text-gray-500">Cirugias hoy</p>
            </div>
          </div>

          {/* Camas y personal */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Bed className="w-5 h-5 text-gray-400" />
                <h4 className="font-medium text-gray-700">Camas</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total</span>
                  <span className="font-medium">{hospital.camas || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Disponibles</span>
                  <span className="font-medium text-green-600">{hospital.camas_disponibles || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Ocupadas</span>
                  <span className="font-medium text-amber-600">{(hospital.camas || 0) - (hospital.camas_disponibles || 0)}</span>
                </div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-gray-400" />
                <h4 className="font-medium text-gray-700">Personal</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Personal medico</span>
                  <span className="font-medium">{hospital.personal_medico || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Hospitales() {
  // Info del usuario para filtrado
  const user = useAuthStore(state => state.user)
  const isOperador = user?.rol === ROLES.OPERADOR
  const userHospitalId = user?.hospital_id

  const [hospitales, setHospitales] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedHospital, setSelectedHospital] = useState(null)
  const [filterOcupacion, setFilterOcupacion] = useState(null) // 'critico', 'alto', 'normal'
  const [filterTipo, setFilterTipo] = useState('')
  const [sortByQuirofanos, setSortByQuirofanos] = useState(false)

  useEffect(() => {
    loadHospitales()
  }, [isOperador, userHospitalId])

  const loadHospitales = async () => {
    try {
      const response = await hospitalesAPI.getAll()
      let data = response.data?.data || response.data || []

      // Filtrar por hospital del operador
      if (isOperador && userHospitalId) {
        data = data.filter(h => h.id === userHospitalId)
      }

      setHospitales(data)
    } catch (error) {
      console.error('Error loading hospitales, usando datos demo:', error)
      // Filtrar datos demo también
      let demoData = [...DEMO_HOSPITALES]
      if (isOperador && userHospitalId) {
        demoData = demoData.filter(h => h.id === userHospitalId)
      }
      setHospitales(demoData)
    } finally {
      setLoading(false)
    }
  }

  // Stats calculadas
  const stats = useMemo(() => {
    const total = hospitales.length
    const totalQuirofanos = hospitales.reduce((sum, h) => sum + (h.quirofanos_totales || 0), 0)
    const quirofanosDisponibles = hospitales.reduce((sum, h) => sum + (h.quirofanos_disponibles || 0), 0)
    const totalCamas = hospitales.reduce((sum, h) => sum + (h.camas || 0), 0)
    const ocupacionPromedio = total > 0
      ? Math.round(hospitales.reduce((sum, h) => sum + (h.ocupacion || 0), 0) / total)
      : 0
    const criticos = hospitales.filter(h => (h.ocupacion || 0) >= 85).length

    return { total, totalQuirofanos, quirofanosDisponibles, totalCamas, ocupacionPromedio, criticos }
  }, [hospitales])

  // Filtrado
  const filteredHospitales = useMemo(() => {
    let result = hospitales.filter(h => {
      const matchSearch = h.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        h.clave?.toLowerCase().includes(search.toLowerCase())

      let matchOcupacion = true
      if (filterOcupacion === 'critico') matchOcupacion = (h.ocupacion || 0) >= 85
      else if (filterOcupacion === 'alto') matchOcupacion = (h.ocupacion || 0) >= 70 && (h.ocupacion || 0) < 85
      else if (filterOcupacion === 'normal') matchOcupacion = (h.ocupacion || 0) < 70

      const matchTipo = !filterTipo || h.tipo === filterTipo

      return matchSearch && matchOcupacion && matchTipo
    })

    if (sortByQuirofanos) {
      result = [...result].sort((a, b) => (b.quirofanos_disponibles || 0) - (a.quirofanos_disponibles || 0))
    }

    return result
  }, [hospitales, search, filterOcupacion, filterTipo, sortByQuirofanos])

  // Stats filtradas
  const filteredStats = useMemo(() => {
    const total = filteredHospitales.length
    const totalQuirofanos = filteredHospitales.reduce((sum, h) => sum + (h.quirofanos_totales || 0), 0)
    const quirofanosDisponibles = filteredHospitales.reduce((sum, h) => sum + (h.quirofanos_disponibles || 0), 0)
    const totalCamas = filteredHospitales.reduce((sum, h) => sum + (h.camas || 0), 0)
    const ocupacionPromedio = total > 0
      ? Math.round(filteredHospitales.reduce((sum, h) => sum + (h.ocupacion || 0), 0) / total)
      : 0
    const criticos = filteredHospitales.filter(h => (h.ocupacion || 0) >= 85).length

    return { total, totalQuirofanos, quirofanosDisponibles, totalCamas, ocupacionPromedio, criticos }
  }, [filteredHospitales])

  // Exportar a CSV
  const exportToCSV = () => {
    const headers = ['Clave', 'Nombre', 'Tipo', 'Quirofanos', 'Disponibles', 'En Uso', 'Camas', 'Ocupacion']
    const rows = filteredHospitales.map(h => [
      h.clave,
      h.nombre,
      h.tipo || 'HGZ',
      h.quirofanos_totales || 0,
      h.quirofanos_disponibles || 0,
      h.quirofanos_en_uso || 0,
      h.camas || 0,
      `${h.ocupacion || 0}%`
    ])

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `hospitales_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Reset filtros
  const resetFilters = () => {
    setSearch('')
    setFilterOcupacion(null)
    setFilterTipo('')
    setSortByQuirofanos(false)
  }

  const getOcupacionColor = (ocupacion) => {
    if (ocupacion >= 85) return 'text-red-600'
    if (ocupacion >= 70) return 'text-amber-600'
    return 'text-green-600'
  }

  const getOcupacionBgColor = (ocupacion) => {
    if (ocupacion >= 85) return 'bg-red-500'
    if (ocupacion >= 70) return 'bg-amber-500'
    return 'bg-green-500'
  }

  const hasActiveFilters = search || filterOcupacion || filterTipo || sortByQuirofanos

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">
              {isOperador ? 'Tu Hospital' : 'Hospitales'}
            </h1>
            {isOperador && user?.hospital_nombre && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {user.hospital_nombre}
              </span>
            )}
          </div>
          <p className="text-gray-500">
            {isOperador ? (
              'Informacion detallada de tu unidad medica'
            ) : (
              <>
                {filteredHospitales.length} de {hospitales.length} hospitales
                {hasActiveFilters && (
                  <button onClick={resetFilters} className="ml-2 text-emerald-600 hover:text-emerald-700 text-sm">
                    (Limpiar filtros)
                  </button>
                )}
              </>
            )}
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
        >
          <Download className="w-4 h-4" />
          Exportar CSV
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Hospitales"
          value={filteredStats.total}
          subtitle="Unidades medicas"
          icon={Building2}
          trend="up"
          trendValue="+2"
          color="emerald"
          showDashOnZero={true}
        />
        <KPICard
          title="Quirofanos"
          value={filteredStats.quirofanosDisponibles}
          subtitle={sortByQuirofanos ? "Ordenando por disponibles" : "Disponibles ahora"}
          icon={Activity}
          trend={filteredStats.total > 0 ? "up" : null}
          trendValue="+3"
          color="green"
          onClick={() => setSortByQuirofanos(!sortByQuirofanos)}
          isActive={sortByQuirofanos}
          showDashOnZero={true}
        />
        <KPICard
          title="Ocupacion Prom."
          value={filteredStats.ocupacionPromedio}
          subtitle="Nivel general"
          icon={TrendingUp}
          trend={filteredStats.total > 0 ? (filteredStats.ocupacionPromedio >= 70 ? "up" : "down") : null}
          trendValue={filteredStats.ocupacionPromedio >= 70 ? "+5%" : "-3%"}
          color={filteredStats.ocupacionPromedio >= 85 ? "red" : filteredStats.ocupacionPromedio >= 70 ? "amber" : "teal"}
          showDashOnZero={true}
          suffix="%"
        />
        <KPICard
          title="Estado Critico"
          value={filteredStats.criticos}
          subtitle={filterOcupacion === 'critico' ? "Filtro activo" : "Hospitales >85% ocupacion"}
          icon={AlertTriangle}
          color="red"
          onClick={() => setFilterOcupacion(filterOcupacion === 'critico' ? null : 'critico')}
          isActive={filterOcupacion === 'critico'}
          showDashOnZero={true}
        />
      </div>

      {/* Filtros y busqueda - solo para admin */}
      {!isOperador && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por nombre o clave..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <select
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Todos los tipos</option>
                <option value="HGZ">HGZ</option>
                <option value="HGR">HGR</option>
              </select>
              <button
                onClick={() => setFilterOcupacion(filterOcupacion === 'alto' ? null : 'alto')}
                className={`px-4 py-2.5 rounded-lg border transition-all ${
                  filterOcupacion === 'alto'
                    ? 'bg-amber-100 border-amber-300 text-amber-700'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                Alta demanda
              </button>
              <button
                onClick={() => setFilterOcupacion(filterOcupacion === 'normal' ? null : 'normal')}
                className={`px-4 py-2.5 rounded-lg border transition-all ${
                  filterOcupacion === 'normal'
                    ? 'bg-green-100 border-green-300 text-green-700'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                Normal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid de hospitales */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredHospitales.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl border border-gray-100 text-center py-16">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg mb-2">No se encontraron hospitales</p>
            <p className="text-gray-400 text-sm mb-4">Intenta ajustar los filtros de busqueda</p>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          filteredHospitales.map((hospital) => (
            <div
              key={hospital.id}
              onClick={() => setSelectedHospital(hospital)}
              className="bg-white border border-gray-100 rounded-xl p-5 hover:shadow-lg hover:border-emerald-200 transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl transition-colors ${
                  hospital.ocupacion >= 85 ? 'bg-red-100 group-hover:bg-red-200' :
                  hospital.ocupacion >= 70 ? 'bg-amber-100 group-hover:bg-amber-200' : 'bg-green-100 group-hover:bg-green-200'
                }`}>
                  <Building2 className={`w-6 h-6 ${
                    hospital.ocupacion >= 85 ? 'text-red-600' :
                    hospital.ocupacion >= 70 ? 'text-amber-600' : 'text-green-600'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-emerald-700 transition-colors">
                        {hospital.nombre}
                      </h3>
                      <p className="text-sm text-gray-500">{hospital.clave}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      hospital.ocupacion >= 85 ? 'bg-red-100 text-red-700' :
                      hospital.ocupacion >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {hospital.tipo || 'HGZ'}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-gray-800">{hospital.quirofanos_totales || 0}</p>
                      <p className="text-xs text-gray-500">Quirofanos</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-green-600">{hospital.quirofanos_disponibles || 0}</p>
                      <p className="text-xs text-gray-500">Disponibles</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Ocupacion</span>
                      <span className={`font-semibold ${getOcupacionColor(hospital.ocupacion)}`}>
                        {hospital.ocupacion || 0}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${getOcupacionBgColor(hospital.ocupacion)}`}
                        style={{ width: `${hospital.ocupacion || 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Bed className="w-3 h-3" />
                      {hospital.camas || 0} camas
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {hospital.personal_medico || 0} personal
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de detalle */}
      {selectedHospital && (
        <HospitalModal hospital={selectedHospital} onClose={() => setSelectedHospital(null)} />
      )}
    </div>
  )
}
